import { useState } from 'react';
import type { ViewResolution } from '@ayumia/contracts';
import { Btn } from '../design-system/primitives';
import { CommandDialog } from '../views/CommandDialog';
import { useLoad } from '../views/useLoad';
import { useAppServices, type ViewCommand } from '../views/ViewContext';
import type { WireEdge } from './graph-model';

const CORRECTIONS_VIEW = 'core.view.relation_corrections';
const PROPOSE = 'ProposeRelationCorrection';

/**
 * « Corriger le lien » depuis l'arête sélectionnée (ADR-116, décision de l'utilisateur du 2026-10-10) :
 * - un lien de correction se clôt à la main, sous l'approbation d'une seconde personne : la proposition
 *   part pré-remplie (arête, relation), seuls le motif et la preuve se saisissent ;
 * - un lien du ledger ne se clôt jamais à la main, seulement par l'inversion de son mouvement ;
 * - un lien porté par un champ se corrige depuis la fiche.
 * La définition de la commande vient de la vue des corrections : sans droit de la lire, rien ne s'affiche.
 * Le serveur revérifie tout ; masquer n'est pas autoriser.
 */
export function LinkCorrection({ edge }: { edge: WireEdge }) {
  const services = useAppServices();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [resolution] = useLoad<ViewResolution | null>(`graph-corrections:${CORRECTIONS_VIEW}`, async () => {
    try {
      return (await services.client.query<ViewResolution>('GetView', { view_ref: CORRECTIONS_VIEW })).data;
    } catch {
      return null;
    }
  });
  const toCorrections = () => services.navigate({ kind: 'view', view_ref: CORRECTIONS_VIEW });

  if (edge.edge_id === undefined) {
    return <p className="ay-graph__muted">Ce lien est porté par un champ de la fiche : il se corrige depuis la fiche.</p>;
  }
  if (edge.provenance !== 'correction') {
    return (
      <>
        <p className="ay-graph__muted">Ce lien vient d’un fait du stock : il se clôt par l’inversion du mouvement qui l’a créé, jamais à la main.</p>
        <Btn variant="ghost" onClick={toCorrections}>
          Ajouter un lien manquant
        </Btn>
      </>
    );
  }
  const propose = resolution.status === 'ready' ? resolution.value?.commands?.find((command) => command.command_name === PROPOSE) : undefined;
  if (propose === undefined) return null;
  if (sent) return <p className="ay-graph__muted">Clôture proposée : une seconde personne doit l’approuver.</p>;
  // Présentation de la clôture : seuls le motif et la preuve se saisissent ; le reste est pré-rempli.
  const closing: ViewCommand = {
    ...propose,
    label: { fr: 'Clore ce lien de correction', en: 'Close this correction link' },
    payload: [
      { field_id: 'correction_kind', label: { fr: 'Nature', en: 'Kind' }, type: 'enumeration', required: true, validation: { enum_values: ['unlink'], enum_labels: { unlink: { fr: 'Clôture d’un lien', en: 'Close a link' } } } },
      ...propose.payload.filter((field) => field.field_id === 'reason' || field.field_id === 'evidence_document_id'),
    ],
  } as ViewCommand;
  return (
    <>
      <Btn variant="ghost" onClick={() => setOpen(true)}>
        Clore ce lien
      </Btn>
      {open && (
        <CommandDialog
          command={closing}
          prefilled={{ correction_kind: 'unlink', relation_id: edge.relation_id, edge_id: edge.edge_id }}
          target={null}
          onClose={() => setOpen(false)}
          onDone={() => {
            setOpen(false);
            setSent(true);
          }}
        />
      )}
    </>
  );
}
